import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import XLSX from 'sheetjs-style';
import logo from '../assets/images/logo_trans.png';

type ExportSheet = {
  data: Record<string, any>[];
  name: string;
};

export type ExportFormat = 'excel' | 'pdf';

export type ExportMetadata = {
  generatedBy?: string;
  partnerCode?: string;
  partnerName?: string;
};

type ExportOptions = {
  fileName: string;
  metadata?: ExportMetadata;
  sheets: ExportSheet[];
  title: string;
};

const APP_NAME = 'MoranjETrade';
const BRAND_DARK = '071820';
const BRAND_GOLD = 'FFB000';
const BORDER = 'D9E1EA';

const headerStyle = {
  font: {
    name: 'Poppins-B',
    sz: 10,
    color: { rgb: 'FFF7E8' },
    bold: true,
  },
  fill: {
    patternType: 'solid',
    fgColor: { rgb: BRAND_DARK },
  },
  border: {
    top: { style: 'thin', color: { rgb: BORDER } },
    bottom: { style: 'thin', color: { rgb: BRAND_GOLD } },
    left: { style: 'thin', color: { rgb: BORDER } },
    right: { style: 'thin', color: { rgb: BORDER } },
  },
  alignment: {
    horizontal: 'center',
    vertical: 'center',
    wrapText: true,
  },
};

const contentStyle = {
  font: {
    name: 'Poppins-L',
    sz: 10,
    color: { rgb: '0D1B2A' },
  },
  border: {
    top: { style: 'thin', color: { rgb: BORDER } },
    bottom: { style: 'thin', color: { rgb: BORDER } },
    left: { style: 'thin', color: { rgb: BORDER } },
    right: { style: 'thin', color: { rgb: BORDER } },
  },
  alignment: {
    vertical: 'center',
    wrapText: true,
  },
};

const totalStyle = {
  ...contentStyle,
  font: {
    name: 'Poppins-B',
    sz: 10,
    color: { rgb: '0D1B2A' },
    bold: true,
  },
  fill: {
    patternType: 'solid',
    fgColor: { rgb: 'FFF4D8' },
  },
};

const emptySheetData = [{ Information: 'Aucune donnée disponible' }];

export const exportPremiumFiles = (options: ExportOptions) => {
  exportPremiumExcel(options);
  exportPremiumPdf(options);
};

export const exportPremiumFile = (options: ExportOptions, format: ExportFormat) => {
  if (format === 'pdf') {
    exportPremiumPdf(options);
    return;
  }

  exportPremiumExcel(options);
};

export const exportPremiumExcel = ({ fileName, metadata, sheets, title }: ExportOptions) => {
  const workbook = XLSX.utils.book_new();
  workbook.Props = {
    Author: APP_NAME,
    Company: getPartnerName(metadata),
    CreatedDate: new Date(),
    Subject: title,
    Title: title,
  };

  sheets.forEach((sheet) => {
    const safeData = sheet.data.length ? sheet.data : emptySheetData;
    const worksheet = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_json(worksheet, safeData, { origin: 'A6' });
    const columnCount = Object.keys(safeData[0] || {}).length;
    const lastColumn = columnName(columnCount - 1);

    XLSX.utils.sheet_add_aoa(
      worksheet,
      [
        [APP_NAME],
        [title],
        [`Rapport : ${sheet.name}`],
        [`Société génératrice : ${getPartnerName(metadata)}${metadata?.partnerCode ? ` (${metadata.partnerCode})` : ''}`],
        [`Généré par : ${metadata?.generatedBy || 'Utilisateur'} - ${moment().format('DD/MM/YYYY HH:mm')}`],
      ],
      { origin: 'A1' },
    );

    worksheet['!merges'] = [
      { s: { c: 0, r: 0 }, e: { c: Math.max(columnCount - 1, 0), r: 0 } },
      { s: { c: 0, r: 1 }, e: { c: Math.max(columnCount - 1, 0), r: 1 } },
      { s: { c: 0, r: 2 }, e: { c: Math.max(columnCount - 1, 0), r: 2 } },
      { s: { c: 0, r: 3 }, e: { c: Math.max(columnCount - 1, 0), r: 3 } },
      { s: { c: 0, r: 4 }, e: { c: Math.max(columnCount - 1, 0), r: 4 } },
    ];

    styleExcelIntro(worksheet);
    styleExcelTable(worksheet, safeData, columnCount);
    worksheet['!cols'] = buildColumnWidths(safeData);
    worksheet['!rows'] = [
      { hpt: 30 },
      { hpt: 24 },
      { hpt: 18 },
      { hpt: 18 },
      { hpt: 18 },
      { hpt: 23 },
    ];
    worksheet['!freeze'] = { xSplit: 0, ySplit: 6 };
    worksheet['!autofilter'] = { ref: `A6:${lastColumn}${safeData.length + 6}` };

    XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(sheet.name));
  });

  XLSX.writeFile(workbook, `${fileName} - ${moment().format('DD-MM-YYYY')}.xlsx`);
};

export const exportPremiumPdf = async ({ fileName, metadata, sheets, title }: ExportOptions) => {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const logoData = await loadImageAsDataUrl(logo);

  sheets.forEach((sheet, index) => {
    if (index > 0) {
      pdf.addPage();
    }

    const safeData = sheet.data.length ? sheet.data : emptySheetData;
    const headers = Object.keys(safeData[0] || {});
    const rows = safeData.map((row) => headers.map((header) => formatCell(row[header])));
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    pdf.setFillColor(7, 24, 32);
    pdf.rect(0, 0, pageWidth, 92, 'F');
    pdf.setFillColor(255, 176, 0);
    pdf.rect(0, 90, pageWidth, 2, 'F');

    if (logoData) {
      pdf.addImage(logoData, 'PNG', pageWidth - 142, 16, 48, 48);
      setPdfOpacity(pdf, 0.045);
      pdf.addImage(logoData, 'PNG', pageWidth / 2 - 155, pageHeight / 2 - 155, 310, 310);
      setPdfOpacity(pdf, 1);
    }

    pdf.setTextColor(255, 247, 232);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text(APP_NAME, 36, 28);
    pdf.setTextColor(255, 176, 0);
    pdf.setFontSize(12);
    pdf.text(title, 36, 50);
    pdf.setTextColor(216, 224, 233);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text(`Rapport : ${sheet.name}`, 36, 68);
    pdf.text(
      `Société génératrice : ${getPartnerName(metadata)}${metadata?.partnerCode ? ` (${metadata.partnerCode})` : ''}`,
      260,
      68,
    );
    pdf.text(
      `Généré par : ${metadata?.generatedBy || 'Utilisateur'} - ${moment().format('DD/MM/YYYY HH:mm')}`,
      520,
      68,
    );

    autoTable(pdf, {
      body: rows,
      head: [headers],
      margin: { left: 28, right: 28 },
      startY: 112,
      styles: {
        cellPadding: 4,
        fontSize: headers.length > 12 ? 5.5 : 7,
        lineColor: [217, 225, 234],
        lineWidth: 0.25,
        overflow: 'linebreak',
        textColor: [13, 27, 42],
      },
      headStyles: {
        fillColor: [7, 24, 32],
        fontStyle: 'bold',
        textColor: [255, 247, 232],
      },
      alternateRowStyles: {
        fillColor: [247, 249, 252],
      },
      didDrawPage: () => {
        const pageSize = pdf.internal.pageSize;
        pdf.setFontSize(8);
        pdf.setTextColor(120, 130, 145);
        pdf.text(
          `${APP_NAME} - ${getPartnerName(metadata)} - ${pdf.getCurrentPageInfo().pageNumber}`,
          pageSize.getWidth() - 220,
          pageSize.getHeight() - 16,
        );
      },
    });
  });

  pdf.save(`${fileName} - ${moment().format('DD-MM-YYYY')}.pdf`);
};

export const getExportMetadata = (user?: any): ExportMetadata => ({
  generatedBy: `${user?.prenom || ''} ${user?.nom || ''}`.trim() || user?.email || 'Utilisateur',
  partnerCode: user?.partenaire?.code_referent,
  partnerName: user?.partenaire?.nom || user?.manager?.nom,
});

const styleExcelIntro = (worksheet: XLSX.WorkSheet) => {
  if (worksheet.A1) {
    worksheet.A1.s = {
      fill: { patternType: 'solid', fgColor: { rgb: BRAND_DARK } },
      font: { name: 'Poppins-B', sz: 18, bold: true, color: { rgb: BRAND_GOLD } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }

  if (worksheet.A2) {
    worksheet.A2.s = {
      fill: { patternType: 'solid', fgColor: { rgb: BRAND_DARK } },
      font: { name: 'Poppins-B', sz: 14, bold: true, color: { rgb: 'FFF7E8' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }

  ['A3', 'A4', 'A5'].forEach((cellRef) => {
    if (worksheet[cellRef]) {
      worksheet[cellRef].s = {
        fill: { patternType: 'solid', fgColor: { rgb: '101F28' } },
        font: { name: 'Poppins-L', sz: 10, color: { rgb: 'D7DEE8' } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }
  });
};

const styleExcelTable = (worksheet: XLSX.WorkSheet, data: any[], columnCount: number) => {
  for (let col = 0; col < columnCount; col++) {
    const column = columnName(col);
    const headerCell = worksheet[`${column}6`];

    if (headerCell) {
      headerCell.s = headerStyle;
    }

    for (let row = 7; row <= data.length + 6; row++) {
      const cell = worksheet[`${column}${row}`];
      if (cell) {
        const firstCell = worksheet[`A${row}`];
        const isTotal = `${firstCell?.v || ''}`.toLowerCase().includes('total');
        cell.s = isTotal ? totalStyle : contentStyle;
      }
    }
  }
};

const buildColumnWidths = (data: Record<string, any>[]) => {
  const headers = Object.keys(data[0] || {});

  return headers.map((header) => {
    const maxContent = data.reduce(
      (max, row) => Math.max(max, formatCell(row[header]).length),
      header.length,
    );

    return { wch: Math.min(Math.max(maxContent + 2, 12), 36) };
  });
};

const sanitizeSheetName = (name: string) =>
  name.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Export';

const columnName = (index: number) => {
  let name = '';
  let current = index + 1;

  while (current > 0) {
    const modulo = (current - 1) % 26;
    name = String.fromCharCode(65 + modulo) + name;
    current = Math.floor((current - modulo) / 26);
  }

  return name;
};

const formatCell = (value: any) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return moment(value).format('DD/MM/YYYY');
  if (typeof value === 'object') return JSON.stringify(value);
  return `${value}`;
};

const getPartnerName = (metadata?: ExportMetadata) =>
  metadata?.partnerName || APP_NAME;

const loadImageAsDataUrl = async (source: string) => {
  try {
    const response = await fetch(source);
    const blob = await response.blob();

    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(`${reader.result}`);
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return '';
  }
};

const setPdfOpacity = (pdf: jsPDF, opacity: number) => {
  const pdfWithState = pdf as any;

  if (pdfWithState.GState && pdfWithState.setGState) {
    pdfWithState.setGState(new pdfWithState.GState({ opacity }));
  }
};
